import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ListNumbers, UsersFour, TennisBall, Trophy, ArrowsClockwise, CaretRight,
} from '@phosphor-icons/react';
import { EVENTS, EVENT_STATUS_LABEL, ALWAYS_OPEN_LADDERS } from '../lib/eventsCatalog';
import { eventAPI } from '../lib/eventApi';
import { tournamentAPI } from '../lib/tournamentApi';
import { roundRobinAPI } from '../lib/roundRobinApi';
import { clubEventsAPI } from '../lib/clubEventsApi';
import SessionDatesStrip from '../components/events/SessionDatesStrip';
import SpotsBar from '../components/events/SpotsBar';
import { LadderRow, SessionRow as MyEventsSessionRow, ClubEventRow as MyClubEventRow } from '../components/events/MyEventsRows';
import { LadderRow as DiscoverLadderRow, TournamentRow as DiscoverTournamentRow, RoundRobinRow as DiscoverRoundRobinRow, ClubEventRow as DiscoverClubEventRow } from '../components/events/EventRows';
import useMyEvents from '../hooks/useMyEvents';
import { ladderAPI } from '../lib/api';

// Map the catalog's icon string → the actual Phosphor component.
const ICONS = {
  ListNumbers,
  UsersFour,
  TennisBall,
  Trophy,
  ArrowsClockwise,
};

const TAB_DEFS = [
  { id: 'mine',     label: 'My Events' },
  { id: 'open',     label: 'Open' },
  { id: 'active',   label: 'In Progress' },
  { id: 'past',     label: 'Past Results' },
  { id: 'catalog',  label: 'Browse Events' },
];

// ---------------------------------------------------------------------------
// Reusable session row (live sessions from /event-sessions)
// ---------------------------------------------------------------------------
function SessionRow({ session, onOpen }) {
  const s = session;
  const isCompleted = s.status === 'completed';
  const isInProgress = s.status === 'in_progress';
  const isOpen = s.status === 'registration_open';

  let badge;
  if (isInProgress)        badge = { text: `Week ${s.current_week}/${s.weeks_planned}`, tone: 'green' };
  else if (isCompleted)    badge = { text: 'Completed', tone: 'gray' };
  else if (isOpen)         badge = { text: 'Registration open', tone: 'amber' };
  else                     badge = { text: s.status.replace(/_/g, ' '), tone: 'amber' };

  const toneClass = {
    green: 'bg-[#015e3f]/10 text-[#015e3f]',
    amber: 'bg-amber-50 text-amber-800 border border-amber-200',
    gray:  'bg-[#e3dcd1]/60 text-[#6a766e]',
  }[badge.tone];

  return (
    <button
      type="button"
      onClick={() => onOpen(s)}
      data-testid={`session-row-${s.id}`}
      className="w-full text-left bg-white border border-[#e3dcd1] rounded-2xl p-4 space-y-2 hover:border-[#015e3f]/40 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm text-[#013523] font-medium truncate">{s.name}</div>
          <div className="text-[11px] text-[#6a766e]">
            {s.gender === 'mens' ? "Men's Night" : 'Ladies Night'} · {s.num_courts} × {s.players_per_court} · {s.weeks_planned} weeks
          </div>
        </div>
        <span className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${toneClass}`}>
          {badge.text}
        </span>
      </div>
      {!isCompleted && <SessionDatesStrip session={s} spots={s.spots} />}
      {isOpen && s.spots && (
        <SpotsBar filled={s.spots.filled} max={s.spots.max} />
      )}
      {isCompleted && (
        <div className="text-[11px] text-[#6a766e]">
          Final standings available.
        </div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Catalog card (static EVENTS catalog — ladders + coming soon)
// ---------------------------------------------------------------------------
function CatalogCard({ event, hasLadderAccess, onOpen }) {
  const Icon = ICONS[event.icon] || ListNumbers;
  const isActive = event.status === 'active';
  const statusLabel = EVENT_STATUS_LABEL[event.status] || event.status;
  // Singles ladder is the only catalog entry whose CTA wording reflects the
  // viewer's membership state. Everything else uses a neutral chevron.
  const ladderCta =
    event.slug === 'mens-singles-ladder'
      ? (hasLadderAccess ? 'Open Ladder' : 'View & Join')
      : null;

  const Wrapper = isActive ? 'button' : 'div';
  const wrapperProps = isActive
    ? {
        type: 'button',
        onClick: () => onOpen(event),
        'data-testid': `event-card-${event.slug}`,
        className:
          'group text-left bg-white border border-[#e3dcd1] rounded-2xl p-5 ' +
          'hover:border-[#015e3f]/40 hover:shadow-md transition-all ' +
          'active:scale-[0.99] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/30',
      }
    : {
        'data-testid': `event-card-${event.slug}`,
        'aria-disabled': 'true',
        className:
          'bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-5 ' +
          'opacity-70 cursor-not-allowed select-none',
      };

  return (
    <Wrapper {...wrapperProps}>
      <div className="flex items-start gap-3">
        <div
          className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${
            !isActive
              ? 'bg-[#e3dcd1]/60 text-[#9aa39d]'
              : event.type === 'tournament'
                ? 'bg-[#fffbf0] text-[#c9a93a]'
                : 'bg-[#015e3f]/10 text-[#015e3f]'
          }`}
          aria-hidden="true"
        >
          <Icon size={22} weight={isActive ? 'fill' : 'regular'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3
              className={`font-serif text-lg leading-tight tracking-tight ${
                isActive ? 'text-[#013523]' : 'text-[#6a766e]'
              }`}
            >
              {event.name}
            </h3>
            <span
              data-testid={`event-status-${event.slug}`}
              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                isActive
                  ? 'bg-[#015e3f]/10 text-[#015e3f]'
                  : 'bg-[#e3dcd1]/60 text-[#6a766e]'
              }`}
            >
              {statusLabel}
            </span>
          </div>
          <p
            className={`text-sm mt-1 ${
              isActive ? 'text-[#2a332c]' : 'text-[#6a766e]'
            }`}
          >
            {event.blurb}
          </p>
        </div>
        {isActive && ladderCta ? (
          <span
            data-testid={`event-cta-${event.slug}`}
            className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium bg-[#015e3f] text-white rounded-full px-3 py-1 mt-1 whitespace-nowrap"
          >
            {ladderCta}
          </span>
        ) : isActive && (
          <CaretRight
            size={18}
            className="text-[#6a766e] group-hover:text-[#015e3f] transition-colors mt-1 shrink-0"
          />
        )}
      </div>
    </Wrapper>
  );
}

// ---------------------------------------------------------------------------
// Empty state for a tab
// ---------------------------------------------------------------------------
function TabEmpty({ title, message, ctaLabel, onCta }) {
  return (
    <div
      className="bg-[#fbfaf8] border border-dashed border-[#e3dcd1] rounded-2xl p-8 text-center"
      data-testid="events-tab-empty"
    >
      <div className="font-medium text-[#013523]">{title}</div>
      <p className="text-sm text-[#6a766e] mt-1">{message}</p>
      {ctaLabel && onCta && (
        <button
          type="button"
          onClick={onCta}
          data-testid="events-tab-empty-cta"
          className="text-sm font-medium text-[#015e3f] mt-3 hover:underline"
        >
          {ctaLabel} →
        </button>
      )}
    </div>
  );
}

// ===========================================================================
// EventsPage — tabbed organization (Slice 2)
// ===========================================================================
export default function EventsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, player, hasLadderAccess, managedSessions } = useAuth();

  const [allSessions, setAllSessions] = useState(null);
  const [allTournaments, setAllTournaments] = useState(null);
  const [allRoundRobins, setAllRoundRobins] = useState(null);
  const [allClubEvents, setAllClubEvents] = useState(null);
  const [liveLadders, setLiveLadders] = useState(null);

  // My Events (ladder + sessions) — same hook the Home accordion uses.
  // Events page renders the full list with NO cap.
  const { loading: mineLoading, rows: mineRows } = useMyEvents({
    user, player, hasLadderAccess, managedSessions,
  });

  // Public list for Open / In Progress / Past Results tabs.
  useEffect(() => {
    let cancelled = false;
    eventAPI.listSessions({ exclude_test: true })
      .catch(() => ({ data: { sessions: [] } }))
      .then((a) => {
        if (cancelled) return;
        setAllSessions(a.data?.sessions || []);
      });
    // Tournaments live in a separate module — pulled in parallel so the
    // Open / In Progress tabs feel like one unified event system.
    tournamentAPI.list()
      .catch(() => ({ data: [] }))
      .then((a) => {
        if (cancelled) return;
        setAllTournaments(a.data || []);
      });
    roundRobinAPI.list()
      .catch(() => ({ data: [] }))
      .then((a) => {
        if (cancelled) return;
        setAllRoundRobins(a.data || []);
      });
    // Club events (non-registration social events). Backend already
    // filters out archived for non-admins.
    clubEventsAPI.list()
      .catch(() => ({ data: { club_events: [] } }))
      .then((a) => {
        if (cancelled) return;
        setAllClubEvents(a.data?.club_events || []);
      });
    // Live ladder statuses — archived ladders are excluded server-side so
    // they vanish from member views; closed ladders come back flagged.
    ladderAPI.listLadders()
      .catch(() => ({ data: { ladders: [] } }))
      .then((a) => {
        if (cancelled) return;
        setLiveLadders(a.data?.ladders || []);
      });
    return () => { cancelled = true; };
  }, []);

  // Catalog ladders overlaid with live status. Archived ladders (absent from
  // the live list) are filtered out; closed ladders keep showing (standings
  // stay visible) but are flagged so the row reads "Closed to new players".
  const visibleLadders = useMemo(() => {
    if (!liveLadders) return ALWAYS_OPEN_LADDERS;
    const statusBySlug = {};
    liveLadders.forEach((l) => { statusBySlug[l.slug] = l.status || 'active'; });
    return ALWAYS_OPEN_LADDERS
      .filter((l) => statusBySlug[l.slug])
      .map((l) => ({ ...l, status: statusBySlug[l.slug] }));
  }, [liveLadders]);

  // Bucket the public list by status (drafts already filtered server-side).
  // Tournaments + always-open ladders are injected so users see "everything
  // currently happening" in one place per tab.
  //
  // Club Events are bucketed by TIME, not status (they only have
  // active/archived; archived is filtered by the API for non-admins):
  //   • starts_at > now                          → Upcoming (Open tab)
  //   • starts_at <= now AND ends_at >= now      → In Progress (when ends_at set)
  //   • ends_at   <  now                         → Past
  //   • ends_at IS NULL AND starts_at < now      → Past
  const { openList, activeList, pastList, openTournaments, activeTournaments, pastTournaments, openRRs, activeRRs, pastRRs, upcomingClubEvents, liveClubEvents, pastClubEvents } = useMemo(() => {
    const list = allSessions || [];
    const tlist = allTournaments || [];
    const rlist = allRoundRobins || [];
    const clist = allClubEvents || [];
    const now = Date.now();
    const upcoming = [];
    const live = [];
    const past = [];
    for (const ce of clist) {
      const startMs = ce.starts_at ? Date.parse(ce.starts_at) : NaN;
      const endMs   = ce.ends_at   ? Date.parse(ce.ends_at)   : NaN;
      if (!Number.isFinite(startMs)) { past.push(ce); continue; }
      if (startMs > now) { upcoming.push(ce); continue; }
      // starts_at <= now
      if (Number.isFinite(endMs)) {
        if (endMs >= now) live.push(ce);
        else              past.push(ce);
      } else {
        // No ends_at → once starts_at has passed, treat as Past.
        past.push(ce);
      }
    }
    return {
      openList:   list.filter((s) => s.status === 'registration_open'),
      activeList: list.filter((s) => s.status === 'in_progress' || s.status === 'registration_closed'),
      pastList:   list.filter((s) => s.status === 'completed'),
      openTournaments:   tlist.filter((t) => t.status === 'registration_open'),
      activeTournaments: tlist.filter((t) => ['bracket_generated', 'in_progress'].includes(t.status)),
      pastTournaments:   tlist.filter((t) => t.status === 'completed'),
      openRRs:   rlist.filter((r) => r.status === 'registration_open'),
      activeRRs: rlist.filter((r) => ['schedule_generated', 'in_progress'].includes(r.status)),
      pastRRs:   rlist.filter((r) => r.status === 'completed'),
      upcomingClubEvents: upcoming.sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at)),
      liveClubEvents:     live.sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at)),
      pastClubEvents:     past.sort((a, b) => Date.parse(b.starts_at) - Date.parse(a.starts_at)),
    };
  }, [allSessions, allTournaments, allRoundRobins, allClubEvents]);

  // Default tab = mine if user has any registered events (ladder OR
  // sessions), else open. URL ?tab= overrides.
  const requestedTab = searchParams.get('tab');
  const defaultTab = (mineRows && mineRows.length > 0) ? 'mine' : 'open';
  const activeTab = TAB_DEFS.find((t) => t.id === requestedTab)?.id || defaultTab;

  const setTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === defaultTab) next.delete('tab');
    else next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  const openSessionDetail = (s) => navigate(`/events/sessions/${s.id}`);
  const openCatalog = (event) => {
    if (event.status !== 'active') return;
    navigate(event.route || `/events/${event.slug}`);
  };

  const loading = mineLoading || allSessions === null || allTournaments === null || allRoundRobins === null || allClubEvents === null;
  const counts = {
    mine:    mineRows ? mineRows.length : 0,
    // Always-open ladders + open tournaments + open sessions + open RRs +
    // upcoming club events all count toward the "Open" tab so the badge
    // reflects what the user will see.
    open:    openList.length + openTournaments.length + openRRs.length + upcomingClubEvents.length + visibleLadders.length,
    active:  activeList.length + activeTournaments.length + activeRRs.length + liveClubEvents.length + visibleLadders.length,
    past:    pastList.length + pastTournaments.length + pastRRs.length + pastClubEvents.length,
    catalog: EVENTS.length,
  };

  return (
    <div className="space-y-6" data-testid="events-page">
      <div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight">
          Events
        </h1>
        <p className="text-sm text-[#6a766e] mt-1">
          Browse club events and ladders.
        </p>
      </div>

      {/* Tab bar — horizontally scrollable on narrow screens */}
      <div
        className="-mx-4 px-4 overflow-x-auto"
        data-testid="events-tabs"
      >
        <div className="inline-flex gap-2 pb-1 min-w-full">
          {TAB_DEFS.map((t) => {
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`events-tab-${t.id}`}
                aria-selected={isActive}
                className={`whitespace-nowrap text-xs uppercase tracking-wider px-3 py-2 rounded-full border transition-colors ${
                  isActive
                    ? 'bg-[#015e3f] text-white border-[#015e3f]'
                    : 'bg-white text-[#2a332c] border-[#e3dcd1] hover:border-[#015e3f]/40'
                }`}
              >
                {t.label}
                {counts[t.id] > 0 && (
                  <span
                    className={`ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-[1.1rem] text-[10px] rounded-full ${
                      isActive ? 'bg-white/20' : 'bg-[#015e3f]/10 text-[#015e3f]'
                    }`}
                  >
                    {counts[t.id]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab body */}
      {loading && (
        <div className="text-sm text-[#6a766e]" data-testid="events-loading">Loading…</div>
      )}

      {!loading && activeTab === 'mine' && (
        <section className="space-y-2" data-testid="events-tab-mine">
          {mineRows.length === 0 ? (
            <TabEmpty
              title="You haven't joined any club events yet."
              message="Browse Open events to find a session that fits your schedule."
              ctaLabel="Browse open events"
              onCta={() => setTab('open')}
            />
          ) : (
            mineRows.map((row) => {
              if (row.kind === 'ladder') {
                return (
                  <LadderRow
                    key="ladder"
                    data={row.data}
                    onClick={() => navigate('/ladder')}
                  />
                );
              }
              if (row.kind === 'tournament') {
                return (
                  <DiscoverTournamentRow
                    key={`t-${row.data.id}`}
                    tournament={row.data}
                    onClick={() => navigate(`/tournaments/${row.data.id}`)}
                  />
                );
              }
              if (row.kind === 'round_robin') {
                return (
                  <DiscoverRoundRobinRow
                    key={`rr-${row.data.id}`}
                    rr={row.data}
                    onClick={() => navigate(`/round-robins/${row.data.id}`)}
                  />
                );
              }
              if (row.kind === 'club_event') {
                return (
                  <MyClubEventRow
                    key={`ce-${row.data.id}`}
                    event={row.data}
                    onClick={() => navigate(`/club-events/${row.data.id}`)}
                  />
                );
              }
              return (
                <MyEventsSessionRow
                  key={row.data.id}
                  session={row.data}
                  onClick={() => navigate(`/events/sessions/${row.data.id}`)}
                  canManage={managedSessions?.includes(row.data.id)}
                  onManage={(s) => navigate(`/admin/events/${s.id}`)}
                />
              );
            })
          )}
        </section>
      )}

      {!loading && activeTab === 'open' && (
        <section className="space-y-2" data-testid="events-tab-open">
          {/* Always-open ladders — appear first so members see them immediately */}
          {visibleLadders.map((l) => (
            <DiscoverLadderRow key={l.slug} ladder={l} onClick={() => navigate(l.route)} />
          ))}
          {/* Open tournaments */}
          {openTournaments.map((t) => (
            <DiscoverTournamentRow key={t.id} tournament={t}
              onClick={() => navigate(`/tournaments/${t.id}`)} />
          ))}
          {/* Open round robins */}
          {openRRs.map((r) => (
            <DiscoverRoundRobinRow key={r.id} rr={r}
              onClick={() => navigate(`/round-robins/${r.id}`)} />
          ))}
          {/* Upcoming club events (non-registration social events) */}
          {upcomingClubEvents.map((ce) => (
            <DiscoverClubEventRow key={ce.id} event={ce} phase="upcoming"
              onClick={() => navigate(`/club-events/${ce.id}`)} />
          ))}
          {/* Open Court Movement sessions */}
          {openList.map((s) => (
            <SessionRow key={s.id} session={s} onOpen={openSessionDetail} />
          ))}
          {openList.length === 0 && openTournaments.length === 0 && openRRs.length === 0 && upcomingClubEvents.length === 0 && (
            <div className="text-xs text-[#6a766e]" data-testid="events-tab-open-no-extras">
              No sessions, tournaments, round robins, or upcoming club events right now.
              The ladders above are always open.
            </div>
          )}
        </section>
      )}

      {!loading && activeTab === 'active' && (
        <section className="space-y-2" data-testid="events-tab-active">
          {/* Ladders are always running */}
          {visibleLadders.map((l) => (
            <DiscoverLadderRow key={l.slug} ladder={l} onClick={() => navigate(l.route)} />
          ))}
          {/* Active tournaments */}
          {activeTournaments.map((t) => (
            <DiscoverTournamentRow key={t.id} tournament={t}
              onClick={() => navigate(`/tournaments/${t.id}`)} />
          ))}
          {/* Active round robins */}
          {activeRRs.map((r) => (
            <DiscoverRoundRobinRow key={r.id} rr={r}
              onClick={() => navigate(`/round-robins/${r.id}`)} />
          ))}
          {/* Club events happening right now */}
          {liveClubEvents.map((ce) => (
            <DiscoverClubEventRow key={ce.id} event={ce} phase="in_progress"
              onClick={() => navigate(`/club-events/${ce.id}`)} />
          ))}
          {/* Active Court Movement sessions */}
          {activeList.map((s) => (
            <SessionRow key={s.id} session={s} onOpen={openSessionDetail} />
          ))}
          {activeList.length === 0 && activeTournaments.length === 0 && activeRRs.length === 0 && liveClubEvents.length === 0 && (
            <div className="text-xs text-[#6a766e]" data-testid="events-tab-active-no-extras">
              No sessions, tournaments, round robins, or club events are running right now.
              The ladders above are always active.
            </div>
          )}
        </section>
      )}

      {!loading && activeTab === 'past' && (
        <section className="space-y-2" data-testid="events-tab-past">
          {pastTournaments.map((t) => (
            <DiscoverTournamentRow key={t.id} tournament={t}
              onClick={() => navigate(`/tournaments/${t.id}`)} />
          ))}
          {pastRRs.map((r) => (
            <DiscoverRoundRobinRow key={r.id} rr={r}
              onClick={() => navigate(`/round-robins/${r.id}`)} />
          ))}
          {pastClubEvents.map((ce) => (
            <DiscoverClubEventRow key={ce.id} event={ce} phase="past"
              onClick={() => navigate(`/club-events/${ce.id}`)} />
          ))}
          {pastList.map((s) => (
            <SessionRow key={s.id} session={s} onOpen={openSessionDetail} />
          ))}
          {pastList.length === 0 && pastTournaments.length === 0 && pastRRs.length === 0 && pastClubEvents.length === 0 && (
            <TabEmpty
              title="No completed events yet."
              message="Once a session or tournament finishes, its final standings will live here."
            />
          )}
        </section>
      )}

      {!loading && activeTab === 'catalog' && (
        <section className="grid gap-3 md:grid-cols-2" data-testid="events-tab-catalog">
          {EVENTS.map((event) => (
            <CatalogCard key={event.slug} event={event} hasLadderAccess={hasLadderAccess} onOpen={openCatalog} />
          ))}
        </section>
      )}
    </div>
  );
}
